import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

// Derive a cart item's fulfillment "medium" from its format/name when the
// caller didn't set one explicitly. Digital products (Game Master PDFs, ePubs,
// iPDFs, interactive, subscriptions) must NOT be tagged 'physical' — otherwise
// checkout wrongly demands a shipping address. Ambiguous merch keeps the
// conservative 'physical' default so its shipping flow is unchanged.
const deriveMediumFromItem = (customItem) => {
  const fmt = (customItem.format || '').toLowerCase();
  const nm = (customItem.name || '').toLowerCase();
  const physicalSignal =
    ['physical', 'paperback', 'print', 'pod'].some((f) => fmt.includes(f)) ||
    nm.includes('paperback') || nm.includes(' print ') || nm.includes('(print') || nm.includes('physical pack');
  if (physicalSignal) return 'physical';
  const digitalSignal =
    ['digital', 'epub', 'ipdf', 'interactive', 'ebook'].some((f) => fmt.includes(f)) ||
    fmt === 'pdf' || fmt.startsWith('subscription') ||
    nm.includes('epub') || nm.includes('ipdf') || nm.includes('e-book') || nm.includes('ebook') || nm.includes('(pdf');
  if (digitalSignal) return 'pdf';
  return 'physical';
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Product definitions matching backend
export const PRODUCTS = {
  single_lesson: {
    id: 'single_lesson',
    name: 'Single Lesson',
    description: 'Download or online access to one lesson',
    listPrice: 4.99,
    salePrice: 1.99,
    currency: 'USD'
  },
  monthly_pack: {
    id: 'monthly_pack',
    name: 'Monthly Pack (4 Lessons)',
    description: 'Download or online access to 4 lessons',
    listPrice: 6.75,
    salePrice: 5.99,
    currency: 'USD'
  },
  mealtime_bundle: {
    id: 'mealtime_bundle',
    name: 'Mealtime Bundle (12 Lessons)',
    description: 'One of each edition per mealtime package',
    listPrice: 13.99,
    salePrice: 12.99,
    currency: 'USD'
  },
  combo_bundle: {
    id: 'combo_bundle',
    name: 'Combo Bundle (24 Lessons)',
    description: 'Multiple books of a single type',
    listPrice: 24.99,
    salePrice: 22.99,
    currency: 'USD'
  },
  instructor_set: {
    id: 'instructor_set',
    name: 'Instructor Set (36 Lessons)',
    description: 'Box set: Breakfast, Lunch, Dinner, Supper (Youth, Adult, Instructor)',
    listPrice: 44.99,
    salePrice: 39.99,
    currency: 'USD'
  },
  gaming_day_pass: {
    id: 'gaming_day_pass',
    name: 'Gaming Day Pass',
    description: '24-hour access to all game modes',
    listPrice: 40.00,
    salePrice: 29.99,
    currency: 'USD'
  },
  // Gift Certificate Products
  gift_certificate_book: {
    id: 'gift_certificate_book',
    name: 'Book Selection Gift Certificate',
    description: 'Redeemable for any Soul Food series book',
    listPrice: 0, // Dynamic pricing
    salePrice: 0, // Set by amount selection
    currency: 'USD',
    isGiftCertificate: true
  }
};

export const CartProvider = ({ children }) => {
  // Initialize cart directly from localStorage to prevent race condition
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('soulFoodCart');
      
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const validItems = parsed.filter(item => 
            item && (item.productId || item.id) && (item.salePrice !== undefined || item.price !== undefined)
          );
          return validItems;
        }
      }
    } catch (e) {
      console.error('[Cart] Error loading cart on init:', e);
      localStorage.removeItem('soulFoodCart');
    }
    return [];
  });
  
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(true); // Already initialized in useState

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('soulFoodCart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Flexible addToCart - handles both PRODUCTS lookup and custom items
  const addToCart = (itemOrProductId, quantity = 1, metadata = {}) => {
    let itemToAdd;
    let uniqueKey;

    // Check if it's a custom item object (from QuickOrder merchandise)
    if (typeof itemOrProductId === 'object' && itemOrProductId !== null) {
      const customItem = itemOrProductId;
      uniqueKey = customItem.uniqueKey || customItem.id || `custom_${Date.now()}`;
      itemToAdd = {
        productId: customItem.productId || customItem.id,
        uniqueKey: uniqueKey,
        id: customItem.id,
        product_id: customItem.product_id || customItem.deliverKey || undefined,
        format: customItem.format || undefined,
        name: customItem.name,
        salePrice: customItem.salePrice ?? customItem.price,
        listPrice: customItem.listPrice ?? customItem.price,
        price: customItem.price,
        quantity: customItem.quantity || 1,
        image: customItem.image,
        isSmallGroupBundle: customItem.isSmallGroupBundle || false,
        isGiftCertificate: customItem.isGiftCertificate || false,
        isParticipantBooklet: customItem.isParticipantBooklet,
        metadata: {
          series: customItem.series || null,
          seriesName: customItem.seriesName || null,
          edition: customItem.edition || 'standard',
          medium: customItem.medium || (customItem.isSmallGroupBundle ? 'bundle' : deriveMediumFromItem(customItem)),
          unit_price: customItem.price,
          // Preserve any custom metadata the caller passed (bundle mix, summary, tier, etc.)
          ...(customItem.metadata || {})
        }
      };
    } else {
      // Standard product lookup
      const productId = itemOrProductId;
      const product = PRODUCTS[productId];
      
      if (!product) {
        console.error('Product not found:', productId);
        return;
      }

      uniqueKey = `${productId}_${metadata.series}_${metadata.edition}_${metadata.medium}`;
      itemToAdd = {
        productId,
        uniqueKey,
        ...product,
        quantity,
        metadata: {
          series: metadata.series || null,
          seriesName: metadata.seriesName || null,
          edition: metadata.edition || 'adult',
          medium: metadata.medium || 'pdf',
          unit_price: metadata.unit_price || product.salePrice
        }
      };
    }

    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.uniqueKey === uniqueKey);
      
      if (existingItem) {
        // Update quantity if same configuration already in cart
        return prevItems.map(item =>
          item.uniqueKey === uniqueKey
            ? { ...item, quantity: item.quantity + (itemToAdd.quantity || 1) }
            : item
        );
      } else {
        // Add new item to cart
        return [...prevItems, itemToAdd];
      }
    });
    
    // Open cart sidebar when item is added - small delay to prevent click-through issues
    setTimeout(() => setIsCartOpen(true), 50);
  };

  const removeFromCart = useCallback((keyToMatch) => {
    setCartItems(prevItems => prevItems.filter(item => {
      const itemKey = item.uniqueKey || item.productId || item.id;
      return itemKey !== keyToMatch;
    }));
  }, []);

  const updateQuantity = useCallback((keyToMatch, newQuantity) => {
    if (newQuantity <= 0) {
      setCartItems(prevItems => prevItems.filter(item => {
        const itemKey = item.uniqueKey || item.productId || item.id;
        return itemKey !== keyToMatch;
      }));
      return;
    }
    
    setCartItems(prevItems => {
      const updated = prevItems.map(item => {
        const itemKey = item.uniqueKey || item.productId || item.id;
        if (itemKey === keyToMatch) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      return updated;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // True if the item is a participant-facing booklet eligible for bundle add-on $1-off.
  // Detected by ID/SKU prefix so existing add-to-cart calls don't need a flag change.
  const isParticipantBookletItem = (item) => {
    if (item.isSmallGroupBundle) return false;
    if (item.isParticipantBooklet === true) return true;
    if (item.isParticipantBooklet === false) return false;
    const id = String(item.id || item.productId || '').toLowerCase();
    return (
      id.startsWith('ihi-') ||
      id.startsWith('4cs-') ||
      id.startsWith('breakfast-ae') ||
      id.startsWith('breakfast-ye') ||
      id.startsWith('holiday-ae') ||
      id.startsWith('holiday-ye')
    );
  };

  const getCartTotal = () => {
    const rawTotal = cartItems.reduce((total, item) => {
      return total + (item.salePrice * item.quantity);
    }, 0);
    const hasBundle = cartItems.some(it => it.isSmallGroupBundle);
    if (!hasBundle) return rawTotal;
    const addonDiscount = cartItems.reduce((d, item) => {
      if (!isParticipantBookletItem(item)) return d;
      return d + (1.00 * item.quantity);
    }, 0);
    return Math.max(0, rawTotal - addonDiscount);
  };

  const getBundleAddonSavings = () => {
    const hasBundle = cartItems.some(it => it.isSmallGroupBundle);
    if (!hasBundle) return 0;
    return cartItems.reduce((d, item) => {
      if (!isParticipantBookletItem(item)) return d;
      return d + (1.00 * item.quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Special function to add gift certificates to cart
  const addGiftCertificateToCart = (giftCertData) => {
    const uniqueKey = `gift_certificate_${giftCertData.certificateType}_${Date.now()}`;
    
    const giftCertItem = {
      productId: `gift_certificate_${giftCertData.certificateType}`,
      uniqueKey: uniqueKey,
      name: `Gift Certificate - ${giftCertData.certificateTypeName}`,
      salePrice: giftCertData.amount,
      listPrice: giftCertData.amount,
      quantity: 1,
      isGiftCertificate: true,
      metadata: {
        certificateType: giftCertData.certificateType,
        certificateTypeName: giftCertData.certificateTypeName,
        recipientName: giftCertData.recipientName,
        recipientEmail: giftCertData.recipientEmail,
        senderName: giftCertData.senderName,
        senderEmail: giftCertData.senderEmail,
        message: giftCertData.message || '',
        amount: giftCertData.amount
      }
    };

    setCartItems(prevItems => [...prevItems, giftCertItem]);
    setIsCartOpen(true);
    
    return uniqueKey;
  };

  // Returns the per-unit effective sale price for an item, factoring in
  // the Small Group Bundle $1-off rule when applicable. This is what
  // Stripe and the order email should bill against.
  const getEffectiveItemPrice = (item) => {
    const base = Number(item.salePrice ?? item.listPrice ?? item.price ?? 0);
    const hasBundle = cartItems.some(it => it.isSmallGroupBundle);
    if (!hasBundle) return base;
    if (!isParticipantBookletItem(item)) return base;
    return Math.max(0, base - 1.00);
  };

  // Check if cart has gift certificates
  const hasGiftCertificates = () => {
    return cartItems.some(item => item.isGiftCertificate);
  };

  const value = {
    cartItems,
    addToCart,
    addGiftCertificateToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getBundleAddonSavings,
    getEffectiveItemPrice,
    getCartCount,
    hasGiftCertificates,
    isCartOpen,
    setIsCartOpen
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
